import { useState, useEffect } from 'react'

interface LoginModalProps {
  onLoginSuccess: () => void
}

interface AuthConfig {
  username: string
  password: string
  updated: string
}

const LoginModal = ({ onLoginSuccess }: LoginModalProps) => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null)

  const STORAGE_KEY = 'configurator_auth'
  const UPDATED_KEY = 'configurator_auth_updated' // Przechowuje timestamp ostatniej zmiany hasła
  const CONFIG_URL = '/xghfsjpsd.json'
  
  // Fallback credentials (if config file fails to load)
  const FALLBACK_USERNAME = 'Unico'
  const FALLBACK_PASSWORD_HASH = 'bc547750b3f219ab0d1e20f45b559742f51c3f02e9ea0ad07c2448213a89bd9f' // Unico2026
  const FALLBACK_UPDATED = '2026-01-26 15:30:00'

  // Load authentication configuration from external file
  useEffect(() => {
    const loadAuthConfig = async () => {
      try {
        // Aggressive cache-busting
        const timestamp = new Date().getTime()
        const random = Math.random()
        const response = await fetch(`${CONFIG_URL}?t=${timestamp}&r=${random}`, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        })
        
        if (!response.ok) {
          throw new Error('Failed to load config')
        }
        
        const config: AuthConfig = await response.json()
        setAuthConfig(config)
        console.log('✅ Dane logowania załadowane z pliku konfiguracyjnego (updated:', config.updated, ')')
        
        // Sprawdź czy hasło się zmieniło (porównaj updated timestamp)
        checkPasswordChanged(config.updated)
      } catch (err) {
        console.warn('⚠️ Nie udało się załadować pliku konfiguracyjnego, używam fallback credentials:', err)
        setAuthConfig(null) // Will use fallback
        
        // Sprawdź fallback updated
        checkPasswordChanged(FALLBACK_UPDATED)
      }
    }
    
    loadAuthConfig()
  }, [])

  // Sprawdź czy hasło zostało zmienione i wyloguj użytkownika jeśli tak
  const checkPasswordChanged = (newUpdated: string) => {
    const storedUpdated = localStorage.getItem(UPDATED_KEY) || sessionStorage.getItem(UPDATED_KEY)
    const isLoggedIn = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY)
    
    if (isLoggedIn && storedUpdated && storedUpdated !== newUpdated) {
      console.warn('⚠️ Hasło zostało zmienione! Wylogowywanie użytkownika...')
      console.warn('Poprzedni timestamp:', storedUpdated)
      console.warn('Nowy timestamp:', newUpdated)
      
      // Wyloguj użytkownika
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(UPDATED_KEY)
      sessionStorage.removeItem(STORAGE_KEY)
      sessionStorage.removeItem(UPDATED_KEY)
      
      // Pokaż informację użytkownikowi
      alert('🔐 Hasło zostało zmienione przez administratora.\n\nMusisz zalogować się ponownie używając nowego hasła.')
      
      // Przeładuj stronę żeby wymusić nowe logowanie
      window.location.reload()
    }
  }

  // Hash password using SHA-256
  const hashPassword = async (password: string): Promise<string> => {
    const encoder = new TextEncoder()
    const data = encoder.encode(password)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    return hashHex
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const passwordHash = await hashPassword(password)
      
      // Use config from file if available, otherwise fallback
      let correctUsername: string
      let correctPasswordHash: string
      
      if (authConfig) {
        // Use credentials from config file
        // Config file should contain the password hash directly (SHA-256)
        correctUsername = authConfig.username
        correctPasswordHash = authConfig.password // Already hashed in JSON file
      } else {
        // Use fallback credentials
        correctUsername = FALLBACK_USERNAME
        correctPasswordHash = FALLBACK_PASSWORD_HASH
      }
      
      if (username === correctUsername && passwordHash === correctPasswordHash) {
        const sessionToken = `${Date.now()}-${Math.random()}`
        
        // Zapisz również timestamp updated aby móc sprawdzić czy hasło się zmieniło
        const updatedTimestamp = authConfig?.updated || FALLBACK_UPDATED
        
        if (rememberMe) {
          localStorage.setItem(STORAGE_KEY, sessionToken)
          localStorage.setItem(UPDATED_KEY, updatedTimestamp)
        } else {
          sessionStorage.setItem(STORAGE_KEY, sessionToken)
          sessionStorage.setItem(UPDATED_KEY, updatedTimestamp)
        }
        
        console.log('✅ Zalogowano z timestamp:', updatedTimestamp)
        onLoginSuccess()
      } else {
        setError('Nieprawidłowy login lub hasło')
        setIsLoading(false)
      }
    } catch (err: any) {
      console.error('Login error:', err)
      setError('Błąd połączenia z serwerem')
      setIsLoading(false)
    }
  }

  return (
    <div className="modal-overlay" style={{ zIndex: 10000 }}>
      <div className="modal-content" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h2 style={{ textAlign: 'center', width: '100%' }}>Logowanie do konfiguratora</h2>
        </div>
        
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="username" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Login
              </label>
              <input
                id="username"
                type="text"
                className="login-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="password" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Hasło
              </label>
              <input
                id="password"
                type="password"
                className="login-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{ marginRight: '8px', cursor: 'pointer', accentColor: '#E5D4B8' }}
                />
                <span>Zapamiętaj logowanie na tym urządzeniu</span>
              </label>
            </div>
            
            {error && (
              <div style={{
                padding: '12px',
                backgroundColor: '#fee',
                color: '#c33',
                borderRadius: '4px',
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#E5D4B8',
                color: '#333',
                border: 'none',
                borderRadius: '0',
                fontSize: '16px',
                fontWeight: '400',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              {isLoading ? 'Logowanie...' : 'Zaloguj się'}
            </button>
            
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <a 
                href="https://unico-kominki.pl" 
                style={{ 
                  color: '#1f2937',
                  textDecoration: 'none',
                  fontSize: '14px'
                }}
                onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                powrót na stronę unico-kominki.pl
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default LoginModal
